/*
 * sticky-add-to-cart.js — floating PDP bar.
 *
 * Mirrors the Purchase Form's ATC state machine. The Purchase Form
 * is the source of truth: it dispatches `pdp:state-change` with
 * `{ state, variant, productId }` whenever the resolved state
 * updates, and the sticky bar listens at the document level and
 * updates its button label, disabled state, hidden variant input,
 * data-atc-state attribute, and price block accordingly.
 *
 * Click routing (driven by `data-atc-state` on the button):
 *   atc / preorder → let the form submit (goes through
 *     sa-product-form → ajax-cart.js → cart drawer)
 *   oos            → button is disabled, no action
 *   enquiry        → preventDefault, dispatch `pdp:open-enquiry`
 *   notify         → preventDefault, dispatch `pdp:open-notify`
 *
 * Visibility:
 *   `body.sticky-atc--visible` is added when the main product
 *   info has scrolled past the viewport top. On configurator PDPs
 *   (where the inline `.sticky-atc--configurator` bar already
 *   covers the fold) an IntersectionObserver on that bar drives
 *   the floating bar instead — it surfaces once the inline bar
 *   scrolls out of view. A separate IntersectionObserver on the
 *   footer toggles `body.footer--in-view` so the sticky bar slides
 *   out as the footer comes into view (CSS rule already in place).
 *
 * Loaded as `type="module"` in snippets/scripts.liquid so we can
 * pull H3.0's money formatter — the price block is re-rendered
 * client-side using the variant in the event payload, the shop's
 * money format string, and the active currency (both passed in
 * via data attributes on #stickyAtc).
 */

import { formatMoney } from '@theme/money-formatting';

/**
 * Mirrors the Liquid `money_without_trailing_zeros` filter — sa-price.liquid
 * renders every price through it, so the JS re-render has to match or the
 * price flickers/changes shape when the sticky bar takes over post-mount.
 * @param {number} cents
 * @param {string} format
 * @param {string} currency
 * @returns {string}
 */
function formatMoneyWithoutTrailingZeros(cents, format, currency) {
  return formatMoney(cents, format, currency).replace(/\.00(?=\D|$)/, '');
}

/** Floating toast above the sticky ATC action cluster (share + wishlist). */
const stickyToastTimers = new WeakMap();

function showStickyAtcToast(toast) {
  if (!toast) return;
  toast.hidden = false;
  void toast.offsetHeight;
  toast.classList.add('sticky-atc__toast--visible');
  const prev = stickyToastTimers.get(toast);
  if (prev) clearTimeout(prev);
  const timer = setTimeout(() => {
    toast.classList.remove('sticky-atc__toast--visible');
    setTimeout(() => {
      toast.hidden = true;
    }, 300);
  }, 2000);
  stickyToastTimers.set(toast, timer);
}

document.addEventListener('sticky-atc:show-toast', (event) => {
  showStickyAtcToast(event.detail?.toast);
});

// Configurator PDPs render both the inline `.sticky-atc--configurator`
// bar (id="stickyAtc") and (when the section is enabled) the floating
// mobile one (id="stickyAtcMobile") — bind each bar found on the page
// independently via the shared `.sticky-atc` class rather than by id.
let stateRequested = false;

function initBar(stickyAtc) {
  const productMain = document.querySelector('.product-main');
  if (!productMain) return;

  const footer =
    document.querySelector('footer') ||
    document.querySelector('[class*="footer-group"]');

  const stickyVariantInput = stickyAtc.querySelector('#sticky-variant-id');
  const button = stickyAtc.querySelector('.sticky-atc__add-btn');
  const label = stickyAtc.querySelector('.sticky-atc__add-btn-label');
  const priceContainer = stickyAtc.querySelector('.sticky-atc__price');
  const productName = stickyAtc.querySelector('.sticky-atc__product-name');

  const moneyFormat = stickyAtc.dataset.moneyFormat || '${{amount}}';
  const currency = stickyAtc.dataset.currency || 'USD';
  const isConfiguratorBar = stickyAtc.classList.contains('sticky-atc--configurator');
  const isInline =
    stickyAtc.classList.contains('sticky-atc--inline') || isConfiguratorBar;

  // ---------- Visibility --------------------------------------
  // Inline bar (configurator column) is always visible in place —
  // component-main-product.css forces it on regardless of body
  // classes, so it needs no scroll handling here.
  //
  // Floating bar: on configurator PDPs the inline bar already covers
  // the fold, so the floating bar should only surface once that
  // inline bar (id="stickyAtc") scrolls out of view — driven by an
  // IntersectionObserver rather than scroll position. Plain PDPs have
  // no inline bar to hand off from, so fall back to the classic "main
  // product info scrolled past the fold" check.
  if (!isInline) {
    const configuratorBar = document.getElementById('stickyAtc');

    if (configuratorBar && 'IntersectionObserver' in window) {
      const configuratorIO = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            // `isIntersecting` alone can't tell "scrolled past" (bar's
            // top has gone above the viewport) apart from "not reached
            // yet" (bar still below the fold) — both report false. Only
            // the former should reveal the floating bar.
            const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            document.body.classList.toggle('sticky-atc--visible', scrolledPast);
          });
        },
        { threshold: 0 }
      );
      configuratorIO.observe(configuratorBar);
    } else {
      const checkStickyVisibility = () => {
        const mainBottom = productMain.getBoundingClientRect().bottom;
        const vh = window.innerHeight;
        document.body.classList.toggle('sticky-atc--visible', mainBottom < vh);
      };
      window.addEventListener('scroll', checkStickyVisibility, { passive: true });
      window.addEventListener('resize', checkStickyVisibility);
      checkStickyVisibility();
    }
  }

  // Footer IO — hide the legacy floating bar as the footer enters view.
  // Configurator section bar is absolutely positioned and scrolls away
  // with main-product, so it does not need footer interception.
  const useFooterHide = !isConfiguratorBar && !stickyAtc.classList.contains('sticky-atc--inline');

  if (useFooterHide && footer && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            document.body.classList.add('footer--in-view');
          } else {
            document.body.classList.remove('footer--in-view');
          }
        });
      },
      { threshold: 0 }
    );
    io.observe(footer);
  }

  // ---------- Product name sync -------------------------------
  // Combined-listing metal switches (product-group-selector) swap the whole
  // product in place — new title, new price — without a page load. The sticky
  // bar's SSR'd name would otherwise stay on the original metal. The shared
  // `.product-purchase-form__product-json` node is refreshed by the group
  // selector BEFORE it fires variant:update, so re-read the authoritative
  // title from it and keep the sticky name in sync.
  function syncProductName() {
    if (!productName) return;
    const node = document.querySelector('.product-purchase-form__product-json');
    if (!node) return;
    try {
      const title = JSON.parse(node.textContent || '')?.title;
      // if (title) productName.textContent = title;
    } catch (_e) {
      /* malformed JSON → leave the current name in place */
    }
  }

  // ---------- State mirror ------------------------------------
  // Apply a resolved state to the sticky bar button. Mirrors the
  // Purchase Form's _applyState shape so the two stay visually
  // consistent (same labels, same modifier classes).
  function applyState(state, variant) {
    if (!button) return;

    button.classList.remove(
      'is-sold-out',
      'is-preorder',
      'enquiry-mode',
      'bis-mode'
    );
    button.dataset.atcState = state;

    switch (state) {
      case 'enquiry':
        if (label) label.textContent = 'Make an Enquiry';
        button.type = 'button';
        button.disabled = false;
        button.classList.add('enquiry-mode');
        button.dataset.variantId = variant?.id ?? '';
        break;

      case 'notify':
        if (label) label.textContent = 'Notify Me When Available';
        button.type = 'button';
        button.disabled = false;
        button.classList.add('bis-mode');
        button.dataset.variantId = variant?.id ?? '';
        break;

      case 'oos':
        if (label) label.textContent = 'Out of Stock';
        button.type = 'button';
        button.disabled = true;
        button.classList.add('is-sold-out');
        break;

      case 'preorder':
        if (label) label.textContent = 'Pre-Order';
        button.type = 'submit';
        button.disabled = false;
        button.classList.add('is-preorder');
        break;

      case 'atc':
      default:
        if (label) label.textContent = 'Add to Cart';
        button.type = 'submit';
        button.disabled = false;
        break;
    }
  }

  // ---------- Price re-render ---------------------------------
  // sa-price snippet logic with is_pdp=true: shows EITHER the
  // sale price (with "Sale" tag + struck compare_at) OR the
  // regular price — never both. Keep the JS output in lockstep
  // with the Liquid so the markup looks identical pre- and
  // post-variant-change.
  function renderPrice(variant) {
    if (!priceContainer || !variant) return;
    const onSale =
      variant.compare_at_price &&
      Number(variant.compare_at_price) > Number(variant.price);

    const wrap = document.createElement('span');
    wrap.className = 'sa-price';

    function fillPriceEl(el, cents) {
      const formatted = formatMoneyWithoutTrailingZeros(cents, moneyFormat, currency);
      if (isInline) {
        const sym = (formatted.match(/^[^\d\s]+/) || [''])[0];
        const amt = formatted.slice(sym.length);
        const currSpan = document.createElement('span');
        currSpan.className = 'sticky-atc__currency';
        currSpan.textContent = sym;
        const amtSpan = document.createElement('span');
        amtSpan.className = 'sticky-atc__amount';
        amtSpan.textContent = amt;
        el.appendChild(currSpan);
        el.appendChild(amtSpan);
      } else {
        el.textContent = formatted;
      }
    }

    if (onSale) {
      const tag = document.createElement('span');
      tag.className = 'product-main__tag';
      tag.textContent = 'Sale';
      wrap.appendChild(tag);

      const old = document.createElement('s');
      old.className = isInline
        ? 'sa-price__old sticky-atc__price-value'
        : 'sa-price__old product-main__price--compare';
      old.dataset.comparePrice = '';
      fillPriceEl(old, variant.compare_at_price);
      wrap.appendChild(old);
    } else {
      const final = document.createElement('span');
      final.className = isInline
        ? 'sa-price__final sticky-atc__price-value'
        : 'sa-price__final product-main__price';
      final.dataset.productPrice = '';
      fillPriceEl(final, variant.price);
      wrap.appendChild(final);
    }

    priceContainer.replaceChildren(wrap);
  }

  // ---------- Wishlist (Swym) sync -----------------------------
  // Configurator metal switches + option changes update product +
  // variant without a full reload. Keep #swym-pdp-button data-* in
  // sync; swym-custom re-checks wishlist state (does not re-bind).
  //
  // IMPORTANT: never rewrite the product URL onto window.location.origin
  // (theme preview = localhost). Swym needs the shop canonical host from
  // Liquid (`https://….myshopify.com`); swapping to localhost after a
  // metal switch makes addToWishList fail and the drawer never opens.
  function absoluteProductUrl(path, fallbackAbsoluteUrl) {
    let shopOrigin = null;
    if (fallbackAbsoluteUrl && /^https?:\/\//i.test(fallbackAbsoluteUrl)) {
      try {
        shopOrigin = new URL(fallbackAbsoluteUrl).origin;
      } catch (_e) {
        shopOrigin = null;
      }
    }
    if (!shopOrigin && stickyAtc.dataset.shopUrl) {
      try {
        shopOrigin = new URL(stickyAtc.dataset.shopUrl).origin;
      } catch (_e) {
        shopOrigin = stickyAtc.dataset.shopUrl.replace(/\/$/, '');
      }
    }
    if (!shopOrigin) shopOrigin = window.location.origin;

    if (!path) {
      return fallbackAbsoluteUrl || `${shopOrigin}${window.location.pathname}`;
    }
    if (/^https?:\/\//i.test(path)) {
      try {
        const parsed = new URL(path);
        // If somehow we already have localhost, re-host onto the shop origin.
        if (/^https?:\/\/(127\.0\.0\.1|localhost)(:\d+)?$/i.test(parsed.origin) && shopOrigin) {
          return `${shopOrigin}${parsed.pathname}${parsed.search}`;
        }
        return path;
      } catch (_e) {
        return path;
      }
    }
    return `${shopOrigin}${path.startsWith('/') ? path : `/${path}`}`;
  }

  function readLiveProductJson() {
    const node = document.querySelector('.product-purchase-form__product-json');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || '');
    } catch (_e) {
      return null;
    }
  }

  function syncWishlistButton(variant, productId) {
    const btn = stickyAtc.querySelector('#swym-pdp-button');
    if (!btn || btn.tagName === 'A') return;

    const liveProduct = readLiveProductJson();
    const nextProductId = String(
      productId || liveProduct?.id || btn.getAttribute('data-product-id') || ''
    );
    const nextVariantId = String(
      variant?.id || btn.getAttribute('data-variant-id') || ''
    );
    if (!nextProductId || !nextVariantId) return;

    const existingUrl = btn.getAttribute('data-product-url') || btn.getAttribute('data-url') || '';
    const productUrl = absoluteProductUrl(liveProduct?.url || '', existingUrl);
    const image =
      variant?.featured_image?.src ||
      (typeof liveProduct?.featured_image === 'string' ? liveProduct.featured_image : liveProduct?.featured_image?.src) ||
      btn.getAttribute('data-image') ||
      '';
    const handle = liveProduct?.handle || btn.getAttribute('data-handle') || '';

    btn.setAttribute('data-product-id', nextProductId);
    btn.setAttribute('data-variant-id', nextVariantId);
    btn.setAttribute('data-product-url', productUrl);
    btn.setAttribute('data-empi', nextProductId);
    btn.setAttribute('data-epi', nextVariantId);
    btn.setAttribute('data-url', productUrl);
    if (image) btn.setAttribute('data-image', image);
    if (handle) btn.setAttribute('data-handle', handle);

    // Fresh product after metal switch — clear stale wishlisted chrome until
    // swym-custom re-checks against the new empi/epi.
    btn.classList.remove('swym-added', 'active');
    btn.setAttribute('aria-label', 'Add to Wishlist');

    document.dispatchEvent(new CustomEvent('swym:pdp-button-updated'));
  }

  // ---------- pdp:state-change listener -----------------------
  // Document-level so it works regardless of whether the
  // Purchase Form is in the same section (PDP grid) or a sibling
  // section. Filter by productId to avoid cross-product bleed
  // when a future quick-add lands on the same page.
  document.addEventListener('pdp:state-change', (event) => {
    const detail = event.detail || {};
    const variant = detail.variant;
    if (!variant) return;

    if (stickyVariantInput) stickyVariantInput.value = variant.id;
    applyState(detail.state || (variant.available ? 'atc' : 'oos'), variant);
    renderPrice(variant);
    syncWishlistButton(variant, detail.productId);
    // syncProductName();
  });

  // ---------- variant:update fallback -------------------------
  // When the PDP uses H3.0's native variant-picker block instead
  // of (or in addition to) the SA Purchase Form, the picker
  // dispatches `variant:update` directly without going through
  // our state machine. The sticky bar can still derive a basic
  // atc/oos state from `variant.available` so the label + price
  // don't go stale. If Purchase Form is also present, its
  // `pdp:state-change` fires AFTER variant:update in the same
  // `_setCurrentVariant` call — so the fuller state wins
  // naturally (last-write).
  document.addEventListener('variant:update', (event) => {
    const variant = event?.detail?.resource;
    if (!variant) return;

    if (stickyVariantInput) stickyVariantInput.value = variant.id;
    applyState(variant.available ? 'atc' : 'oos', variant);
    renderPrice(variant);
    syncWishlistButton(variant, event?.detail?.data?.productId || event?.detail?.data?.newProduct?.id);
    // syncProductName();
  });

  // ---------- Selections-complete listener ---------------------
  // Figma "PDP/ all selections made" — mirrors product-purchase-
  // form.js's _bindSelectionsComplete so the sticky bar's own button
  // also picks up the "ready" blue state (one-way for the rest of
  // the page's life; see assets/product-configurator-progress.js).
  document.addEventListener('pdp:selections-complete', () => {
    button?.classList.add('is-ready');
  });

  // ---------- Click routing -----------------------------------
  // Match the Purchase Form's _bindAtcStateClicks: enquiry/notify
  // states preventDefault + dispatch their modal-open event;
  // atc/preorder fall through to natural form submit; oos is
  // disabled so click won't fire anyway.
  //
  // Scroll-to-main-ATC (section setting) takes priority over all of
  // that — some PDPs need fields the sticky mini-form can't carry
  // (engraving, ring size, etc.), so the merchant can opt the button
  // out of submitting anything and just hand the shopper back to the
  // real Add to Cart control instead. Configurator PDPs pin their
  // purchase controls in `.sticky-atc--configurator` (inline bar,
  // not the Purchase Form's own ATC — that block's inline ATC is
  // normally off there); fall back to the Purchase Form's button for
  // non-configurator PDPs.
  const scrollToMainAtc = stickyAtc.dataset.scrollToMainAtc === 'true';

  if (button && scrollToMainAtc) {
    button.addEventListener('click', (event) => {
      event.preventDefault();

      const mainAtcTarget =
        document.querySelector('.sticky-atc--configurator') ||
        document.querySelector('[data-purchase-form-atc]');
      if (!mainAtcTarget) return;
      mainAtcTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  } else if (button) {
    button.addEventListener('click', (event) => {
      const state = button.dataset.atcState;
      if (state !== 'enquiry' && state !== 'notify') return;
      event.preventDefault();

      const variantId = stickyVariantInput?.value;
      const name = state === 'enquiry' ? 'pdp:open-enquiry' : 'pdp:open-notify';
      button.dispatchEvent(
        new CustomEvent(name, {
          bubbles: true,
          detail: {
            variant: variantId ? { id: Number(variantId) } : null,
          },
        })
      );
    });
  }

  // ---------- Share = copy product link + toast ---------------
  // Works for guests and signed-in shoppers alike. Reuse
  // absoluteProductUrl so a theme-preview localhost origin is
  // normalised to the canonical shop origin before it hits the
  // clipboard.
  // POST a hidden customer-tag form ([data-share-tag-form] /
  // [data-save-tag-form]) to /contact in the background. Best-effort
  // and once per form per page load — tags append server-side, so a
  // repeat would be harmless, just wasteful.
  const postCustomerTagForm = (form) => {
    if (!form || form.dataset.tagPosted) return;
    form.dataset.tagPosted = 'true';
    try {
      fetch(form.getAttribute('action') || '/contact#contact_form', {
        method: 'POST',
        body: new FormData(form),
        keepalive: true,
      }).catch(() => {});
    } catch (_e) {
      /* tagging is best-effort */
    }
  };

  const shareBtn = stickyAtc.querySelector('[data-share-btn]');
  if (shareBtn) {
    const showShareToast = () => {
      showStickyAtcToast(stickyAtc.querySelector('[data-share-toast]'));
    };

    const copyText = async (text) => {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (_e) {
        // Clipboard API unavailable (insecure context / denied) — fallback.
        const tmp = document.createElement('textarea');
        tmp.value = text;
        tmp.setAttribute('readonly', '');
        tmp.style.position = 'absolute';
        tmp.style.left = '-9999px';
        document.body.appendChild(tmp);
        tmp.select();
        let ok = false;
        try {
          ok = document.execCommand('copy');
        } catch (_e2) {
          ok = false;
        }
        document.body.removeChild(tmp);
        return ok;
      }
    };

    shareBtn.addEventListener('click', async () => {
      const tileTrigger = document.querySelector('[data-design-share-trigger]');
      if (tileTrigger) {
        tileTrigger.click();
        return;
      }

      const raw = shareBtn.getAttribute('data-share-url') || '';
      let path = raw;
      try {
        const parsed = new URL(raw);
        path = parsed.pathname + parsed.search;
      } catch (_e) {
        /* raw is already a path or empty */
      }
      let url = absoluteProductUrl(path, '') || window.location.href;

      // Carry the live variant id so the shared link reopens the exact
      // configured variant (stone / shape / metal), not the default.
      const variantId =
        stickyVariantInput?.value ||
        productMain.querySelector('input[name="id"]')?.value ||
        '';
      if (variantId) {
        try {
          const u = new URL(url);
          u.searchParams.set('variant', variantId);
          url = u.toString();
        } catch (_e) {
          url += `${url.includes('?') ? '&' : '?'}variant=${encodeURIComponent(variantId)}`;
        }
      }

      const ok = await copyText(url);
      if (ok) showShareToast();

      // Signed-in shoppers carry a hidden customer form (Liquid only
      // renders it when `customer` exists) that appends the
      // "design-shared" tag to their profile via /contact. Fire it
      // once per page load, in the background — a failure (e.g.
      // Shopify bot-challenge intercept) must never affect share UX.
      postCustomerTagForm(stickyAtc.querySelector('[data-share-tag-form]'));
    });
  }

  // ---------- Save = wishlist add tags the customer -------------
  // Same hidden-customer-form mechanics as share, but hooked on
  // Swym's added-to-wishlist success event instead of the click:
  // swym-custom.liquid handles the click at document capture with
  // stopImmediatePropagation (click listeners here would never run),
  // and the success event means "design-saved" only applies when a
  // save actually went through. Guests have neither the form nor the
  // ability to add (login popup), so this is signed-in only.
  const saveTagForm = stickyAtc.querySelector('[data-save-tag-form]');
  if (saveTagForm) {
    const bindSaveTag = (swat) => {
      if (!swat.evtLayer || saveTagForm.dataset.saveTagBound) return;
      saveTagForm.dataset.saveTagBound = 'true';
      const addedEvent =
        (swat.JSEvents && swat.JSEvents.addedToWishlist) || 'sw:addedtowishlist';
      swat.evtLayer.addEventListener(addedEvent, () => {
        postCustomerTagForm(saveTagForm);
      });
    };
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(bindSaveTag);
    // Late registration: if Swym already finished, the callbacks queue
    // won't re-run (same fallback swym-custom.liquid uses).
    if (window._swat) bindSaveTag(window._swat);
  }

  // ---------- Initial variant id sync -------------------------
  // Seed the sticky bar's hidden input from the main product
  // form's id input immediately so the SSR markup is in sync
  // before any event arrives.
  const mainVariantInput = productMain.querySelector('input[name="id"]');
  if (mainVariantInput && stickyVariantInput) {
    stickyVariantInput.value = mainVariantInput.value;
  }

  // ---------- Late-subscriber handshake -----------------------
  // The Purchase Form dispatches its initial `pdp:state-change`
  // during construction (also at DCL), BEFORE the listener
  // above was attached — so the first paint of the sticky bar
  // is stuck on the SSR'd atc/oos state, missing Made-to-Order
  // (Enquiry), Pre-Order, and Notify Me. Pinging
  // `pdp:request-state` makes the Purchase Form re-broadcast
  // its current state so we catch up immediately. Fire on the
  // next microtask so all listeners on the page (this one
  // included) are guaranteed registered before the re-fire
  // arrives.
  queueMicrotask(() => {
    if (stateRequested) return;
    stateRequested = true;
    document.dispatchEvent(new CustomEvent('pdp:request-state'));
  });
}

function init() {
  document.querySelectorAll('.sticky-atc').forEach(initBar);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
